import { useRef } from "react";
import { cx } from "../cx";

export interface SegmentOption<V extends string> {
  value: V;
  label: string;
  /** Sets `lang` on the option, for the DE|EN switcher. */
  lang?: string;
}

/**
 * One exclusive choice drawn as a row of segments: the Interface Language, the
 * Review view modes, the stability setting. `boxed` is the bordered pill
 * (DE|EN), `text` the underlined mono words above the Review columns.
 *
 * It is a real radio group, which means the whole group is one tab stop and
 * the arrow keys move between the segments (WAI-ARIA's radio pattern). Giving
 * every segment its own tab stop would make a two-option switch cost two tabs
 * on the way to the content behind it, and it would not tell a screen reader
 * that the options exclude each other.
 */
export function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  label,
  appearance = "boxed",
  className = "",
}: {
  options: ReadonlyArray<SegmentOption<V>>;
  value: V;
  onChange: (value: V) => void;
  /** Accessible group name. */
  label: string;
  appearance?: "boxed" | "text";
  className?: string;
}) {
  const boxed = appearance === "boxed";
  const group = useRef<HTMLDivElement>(null);
  const selected = options.findIndex((o) => o.value === value);

  /**
   * Moves the selection and the focus together, which is how a radio group
   * behaves. The index wraps first and the wrapped one drives both: an
   * ArrowLeft on the first segment is index -1, and reading the buttons at -1
   * would leave the focus behind while the selection jumped to the last one.
   */
  const moveTo = (index: number) => {
    const wrapped = ((index % options.length) + options.length) % options.length;
    const target = options[wrapped];
    if (!target) return;
    onChange(target.value);
    group.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[wrapped]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const from = selected < 0 ? 0 : selected;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        moveTo(from + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        moveTo(from - 1);
        break;
      case "Home":
        moveTo(0);
        break;
      case "End":
        moveTo(options.length - 1);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label={label}
      data-component="SegmentedControl"
      data-appearance={appearance}
      onKeyDown={onKeyDown}
      className={cx(
        "inline-flex items-center font-mono text-label",
        boxed ? "h-control rounded-sm border border-line bg-surface" : "gap-3.5",
        className,
      )}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            // Roving tabindex: only the checked segment is in the tab order,
            // and the first one when nothing is checked yet.
            tabIndex={active || (selected < 0 && index === 0) ? 0 : -1}
            lang={option.lang}
            onClick={() => onChange(option.value)}
            className={cx(
              boxed
                ? cx(
                    "h-full px-2.5",
                    index > 0 && "border-l border-line",
                    active ? "bg-accent-soft text-accent" : "text-ink-2 hover:text-ink",
                  )
                : cx(
                    "border-b pb-0.5",
                    active ? "border-ink text-ink" : "border-transparent text-ink-3 hover:text-ink",
                  ),
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
